(() => {
  'use strict';

  const CONTACT_CONTEXT_KEY = 'pw-product-contact-context';
  const PENDING_LEAD_KEY = 'pw-product-contact-pending-lead';
  const topicHelp = {
    general: 'Omschrijf uw vraag zo volledig mogelijk.',
    'product-question': 'Het product van de vorige pagina is alvast ingevuld. Stel hieronder uw vraag over dit apparaat.',
    'product-advice': 'Vertel welk apparaat u zoekt, wat belangrijk is en welk budget u ongeveer in gedachten heeft.',
    'order-online': 'Houd uw bestelnummer en het e-mailadres van de bestelling bij de hand.',
    'order-store': 'Vul waar mogelijk het factuurnummer of de aankoopdatum in.',
    delivery: 'Vul uw bestelnummer en de geplande of werkelijke leverdatum in.',
    repair: 'Merk en typenummer zijn verplicht. Voeg ook serienummer en foutcode toe wanneer u die kunt vinden.',
    warranty: 'Geef aan waar en wanneer het product is gekocht en voeg de apparaatgegevens toe.',
    return: 'Vul het bestelnummer in en beschrijf welk product u wilt retourneren, annuleren of ruilen.',
    complaint: 'Beschrijf wat er is gebeurd en welke oplossing u van ons verwacht.',
    compliment: 'Leuk om te horen. Vertel gerust wie of wat u positief is opgevallen.',
    business: 'Beschrijf uw organisatie, aanvraag en gewenste aantallen of planning.',
    other: 'Omschrijf uw vraag zo volledig mogelijk.'
  };

  const cleanText = (value, maximumLength = 500) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, maximumLength);

  const sendAnalyticsEvent = (eventName, parameters = {}) => {
    const payload = Object.fromEntries(
      Object.entries(parameters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    );

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  };

  const analyticsParameters = (context) => ({
    item_id: context.item_id || context.sku || context.variant_id,
    item_name: context.product_name,
    item_brand: context.brand,
    item_category: context.category,
    item_variant: context.variant,
    contact_source: context.contact_source || 'product_page'
  });

  const readStoredContext = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(CONTACT_CONTEXT_KEY) || 'null');
    } catch (_error) {
      return null;
    }
  };

  const readProductContext = () => {
    const params = new URLSearchParams(window.location.search);
    const context = {
      contact_topic: cleanText(params.get('contact_topic'), 50),
      product_name: cleanText(params.get('product_name')),
      brand: cleanText(params.get('brand')),
      category: cleanText(params.get('category')),
      sku: cleanText(params.get('sku'), 120),
      variant: cleanText(params.get('variant')),
      variant_id: cleanText(params.get('variant_id'), 120),
      item_id: cleanText(params.get('item_id'), 120),
      product_url: cleanText(params.get('product_url'), 1000),
      contact_source: cleanText(params.get('contact_source'), 80)
    };

    const hasUrlContext = context.contact_topic === 'product' || context.product_name || context.product_url;
    if (hasUrlContext) return context;

    const stored = readStoredContext();

    try {
      const referrer = new URL(document.referrer);
      const sameOrigin = referrer.origin === window.location.origin;
      const fromProduct = sameOrigin && referrer.pathname.startsWith('/products/');
      const fromContact = sameOrigin && referrer.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '');

      if (fromProduct && stored && (stored.product_name || stored.product_url)) return stored;
      if (fromContact && stored && (stored.product_name || stored.product_url)) return stored;

      if (fromProduct) {
        return {
          contact_topic: 'product',
          product_name: '',
          brand: '',
          category: '',
          sku: '',
          variant: '',
          variant_id: referrer.searchParams.get('variant') || '',
          item_id: referrer.searchParams.get('variant') || '',
          product_url: referrer.toString(),
          contact_source: 'product_page_referrer'
        };
      }
    } catch (_error) {
      // Geen bruikbare verwijzende productpagina gevonden.
    }

    return null;
  };

  const ensureProductQuestionOption = (topic) => {
    let option = [...topic.options].find((item) => item.dataset.topic === 'product-question');
    if (option) return option;

    option = document.createElement('option');
    option.value = 'Vraag over een product';
    option.dataset.topic = 'product-question';
    option.textContent = 'Vraag over een product';

    const productAdviceOption = [...topic.options].find((item) => item.dataset.topic === 'product-advice');
    topic.insertBefore(option, productAdviceOption || topic.options[1] || null);
    return option;
  };

  const ensureHiddenContextFields = (form, context) => {
    const fields = {
      Productnaam: context.product_name,
      'Merk product': context.brand,
      Productcategorie: context.category,
      'SKU product': context.sku,
      'Variant product': context.variant,
      'Variant-ID product': context.variant_id,
      'Product-URL': context.product_url,
      'Bron contactvraag': context.contact_source || 'product_page'
    };

    Object.entries(fields).forEach(([label, value]) => {
      if (!value) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `contact[${label}]`;
      input.value = cleanText(value, 1000);
      input.dataset.productContextField = label;
      form.append(input);
    });
  };

  const renderProductContext = (page, context) => {
    const productLinkField = page.querySelector('input[name="contact[Product of webadres]"]');
    const productPanel = productLinkField?.closest('[data-contact-panel]');
    if (!productPanel) return;

    const topics = new Set((productPanel.dataset.showFor || '').split(/\s+/).filter(Boolean));
    topics.add('product-question');
    productPanel.dataset.showFor = [...topics].join(' ');

    const budgetField = page.querySelector('input[name="contact[Indicatief budget]"]')?.closest('.pw-contact-field');
    if (budgetField) budgetField.dataset.hideForProductQuestion = 'true';

    const categoryField = page.querySelector('input[name="contact[Productcategorie]"]');
    if (categoryField && context.category && !categoryField.value) categoryField.value = context.category;

    const productDescription = [
      context.product_name,
      context.sku ? `SKU: ${context.sku}` : '',
      context.variant ? `Variant: ${context.variant}` : ''
    ].filter(Boolean).join(' · ');

    if (productLinkField && !productLinkField.value) {
      productLinkField.value = productDescription || context.product_url;
      productLinkField.dataset.contextProductUrl = context.product_url || '';
    }

    const grid = productPanel.querySelector('.pw-contact-grid');
    if (!grid || grid.querySelector('[data-contact-product-summary]')) return;

    const summary = document.createElement('div');
    summary.className = 'pw-contact-consent pw-contact-field--full';
    summary.dataset.contactProductSummary = 'true';

    const heading = document.createElement('strong');
    heading.textContent = 'Uw vraag gaat over';
    summary.append(heading);

    const product = document.createElement('p');
    product.style.margin = '6px 0 0';
    product.textContent = productDescription || 'Het product van de vorige pagina';
    summary.append(product);

    if (context.product_url) {
      const link = document.createElement('a');
      link.href = context.product_url;
      link.textContent = 'Bekijk het product';
      link.style.display = 'inline-block';
      link.style.marginTop = '6px';
      summary.append(link);
    }

    grid.prepend(summary);
  };

  const initializeContactPage = (page) => {
    if (!(page instanceof Element) || page.dataset.contactReady === 'true') return;
    page.dataset.contactReady = 'true';

    const topic = page.querySelector('[data-contact-topic]');
    const help = page.querySelector('[data-contact-topic-help]');
    const panels = [...page.querySelectorAll('[data-contact-panel]')];
    const messageStep = page.querySelector('[data-message-step]');
    const form = page.querySelector('form.pw-contact-form');
    if (!topic || !form) return;

    const productQuestionOption = ensureProductQuestionOption(topic);
    const productContext = readProductContext();

    if (productContext) {
      renderProductContext(page, productContext);
      ensureHiddenContextFields(form, productContext);
      topic.value = productQuestionOption.value;

      try {
        window.sessionStorage.setItem(CONTACT_CONTEXT_KEY, JSON.stringify(productContext));
      } catch (_error) {
        // De URL blijft als terugvalroute beschikbaar.
      }

      sendAnalyticsEvent('product_contact_view', analyticsParameters(productContext));
    }

    const update = () => {
      const selected = topic.selectedOptions[0];
      const key = selected?.dataset.topic || '';

      panels.forEach((panel) => {
        const topics = (panel.dataset.showFor || '').split(/\s+/).filter(Boolean);
        const visible = topics.includes(key);
        panel.hidden = !visible;
        panel.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = !visible;
          const requiredTopics = (field.dataset.requiredTopics || '').split(/\s+/).filter(Boolean);
          field.required = visible && (field.hasAttribute('data-required-when-visible') || requiredTopics.includes(key));
        });
      });

      page.querySelectorAll('[data-hide-for-product-question="true"]').forEach((field) => {
        const hidden = key === 'product-question';
        field.hidden = hidden;
        field.querySelectorAll('input, select, textarea').forEach((input) => {
          input.disabled = hidden;
        });
      });

      if (help) help.textContent = topicHelp[key] || 'Na uw keuze verschijnen de relevante aanvullende vragen.';
      if (messageStep) {
        const visiblePanelCount = panels.filter((panel) => !panel.hidden).length;
        messageStep.textContent = `${3 + visiblePanelCount}. Uw bericht`;
      }
    };

    topic.addEventListener('change', update);
    update();

    let started = false;
    form.addEventListener('input', (event) => {
      if (started || !(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement || event.target instanceof HTMLTextAreaElement)) return;
      if (event.target.type === 'hidden') return;
      started = true;
      if (productContext) sendAnalyticsEvent('product_contact_start', analyticsParameters(productContext));
    });

    form.addEventListener('submit', () => {
      if (!productContext) return;
      try {
        window.sessionStorage.setItem(CONTACT_CONTEXT_KEY, JSON.stringify(productContext));
        window.sessionStorage.setItem(PENDING_LEAD_KEY, JSON.stringify(productContext));
      } catch (_error) {
        // Geen aanvullende actie nodig.
      }
      sendAnalyticsEvent('product_contact_submit', analyticsParameters(productContext));
    });

    const success = page.querySelector('.pw-contact-alert--success');
    if (success) {
      let successfulContext = null;
      try {
        successfulContext = JSON.parse(window.sessionStorage.getItem(PENDING_LEAD_KEY) || 'null');
      } catch (_error) {
        successfulContext = null;
      }

      if (successfulContext) {
        sendAnalyticsEvent('generate_lead', {
          ...analyticsParameters(successfulContext),
          lead_source: 'product_contact_form'
        });
        try {
          window.sessionStorage.removeItem(PENDING_LEAD_KEY);
          window.sessionStorage.removeItem(CONTACT_CONTEXT_KEY);
        } catch (_error) {
          // Geen aanvullende actie nodig.
        }
      }
    }
  };

  const initializeAll = () => document.querySelectorAll('[data-pw-contact-page]').forEach(initializeContactPage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }
})();
